import { Box, Paper } from "@mantine/core";
import { type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode, useCallback, useRef, useState } from "react";

// An editor and a panel abreast with a grip between them, used by /python and /javascript. What the grip sets is a
// custom property on the row, never a width on the panel itself, so the container query in global.css can still put the
// panel below the editor at the breakpoint every other row on the site stacks at — a width in a style prop would
// outrank the stylesheet and leave a stacked panel wearing a dragged width.
export function Panes({ panel, children }: { panel: ReactNode; children: ReactNode }) {
  // A pane width is this screen's business rather than this script's, so it is not a field the share link carries.
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const { pane, grip } = useGrip("row", panelWidth, setPanelWidth, { first: LEFT_OF_PANEL, second: MIN_PANEL_WIDTH });

  return (
    <Box ref={pane} className="split-panes" style={{ "--split-panel-width": `${panelWidth}px` } as CSSProperties}>
      {children}

      <div
        className="split-grip"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the variables panel"
        tabIndex={0}
        {...grip}
      />

      <Paper
        withBorder
        shadow="sm"
        radius="md"
        className="split-panel"
        style={{ position: "relative", overflow: "hidden" }}
      >
        {/* Absolute, as the editor's contents are: a deep object would otherwise make the panel taller than the row. */}
        <Box style={PANE_INSET}>{panel}</Box>
      </Paper>
    </Box>
  );
}

// Two panes and the grip that divides them, side by side or one above the other. `Panes` above is the fixed row
// /python and /javascript are built from; this is the general pair, which /sql nests one inside the other. Both share
// the grip, since a drag that captures the pointer and arrow keys that answer to it are the same work either way.
export function Split({ direction, label, initial, floors, first, second }: SplitProps) {
  // Not a field of the share link, for the same reason `Panes` keeps its own width off it: a pane size is the screen's.
  const [size, setSize] = useState(initial);
  const { pane, grip } = useGrip(direction, size, setSize, floors);

  return (
    <Box
      ref={pane}
      className="split-pair"
      data-direction={direction}
      style={{ "--split-size": `${size}px` } as CSSProperties}
    >
      <div className="split-first">{first}</div>

      <div
        className="split-grip"
        data-direction={direction}
        role="separator"
        // The separator's own orientation, which is across the direction the panes are laid out in.
        aria-orientation={direction === "row" ? "vertical" : "horizontal"}
        aria-label={label}
        tabIndex={0}
        {...grip}
      />

      <div className="split-second">{second}</div>
    </Box>
  );
}

interface SplitProps {
  // How the two are laid out: "row" puts the second pane to the right, "column" puts it below.
  direction: Direction;
  label: string;
  initial: number;
  floors: Floors;
  first: ReactNode;
  second: ReactNode;
}

type Direction = "row" | "column";

// How little each pane may be dragged to. The first pane's floor is spelled with the grip's own width in it, since
// both stand on the side of the split the second pane is not being dragged into.
interface Floors {
  first: number;
  second: number;
}

// The drag, the arrow keys and the clamping, which is the whole of what a grip between two panes does. The second
// pane is the one that carries a size, so what is dragged is always the distance from the far edge back to the grip.
function useGrip(direction: Direction, size: number, setSize: (size: number) => void, floors: Floors) {
  const pane = useRef<HTMLDivElement>(null);
  const row = direction === "row";

  const resize = useCallback((next: number) => {
    const box = pane.current?.getBoundingClientRect();
    if (!box) return;
    const whole = row ? box.width : box.height;
    const most = Math.max(floors.second, whole - floors.first);
    setSize(Math.min(Math.max(next, floors.second), most));
  }, [floors.first, floors.second, row, setSize]);

  const onPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    // Capture keeps the drag on the grip once the pointer is over a pane, and takes the text selection with it.
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const box = pane.current?.getBoundingClientRect();
    if (box) resize(row ? box.right - event.clientX : box.bottom - event.clientY);
  }, [resize, row]);

  const onPointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    // A pointer released over the grip that was never pressed on it holds no capture, and releasing one throws.
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  // A grip that only answers to a pointer is a size somebody without one cannot set.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const grow = row ? "ArrowLeft" : "ArrowUp";
    const shrink = row ? "ArrowRight" : "ArrowDown";
    const step = event.key === grow ? KEY_STEP : event.key === shrink ? -KEY_STEP : 0;
    if (step === 0) return;
    event.preventDefault();
    resize(size + step);
  };

  return { pane, grip: { onPointerDown, onPointerMove, onPointerUp, onKeyDown } };
}

// A column filling the pane, so a heading holds the top and what is below it takes the rest and scrolls in it.
export const PANE_INSET: CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
};

// The panel's width, and the floors either side of it, which `.split-panes` in global.css holds it to as well.
const DEFAULT_PANEL_WIDTH = 320;
const MIN_PANEL_WIDTH = 160;

// The editor's own floor plus the grip beside it, since both stand to the left of whatever the panel is dragged to.
const LEFT_OF_PANEL = 208;

const KEY_STEP = 24;
